import { Fragment } from 'react';
import styled from 'styled-components';

import CircularProgress from 'components/shared/CircularProgress';
import { useReferralsInsightsActions } from 'stores/referralsInsightsStore';
import { Label } from 'styles/typography';
import ChevronRightBold from 'svg/ChevronRightBold';

import { formatPercentage, formatResponseTime } from '../constants';

type MetricConfig = {
  label: string;
  value: string;
  color: 'green' | 'red' | 'default';
  subtitle: string;
  onClick?: () => void;
};

type Props = {
  acceptanceRate: number | null;
  denialRate: number | null;
  avgResponseTimeSeconds: number | null;
  acceptedCount: number;
  deniedCount: number;
  totalReferrals: number;
  respondedCount: number;
  loading: boolean;
};

export default function ReferralHighlightMetrics({
  acceptanceRate,
  denialRate,
  avgResponseTimeSeconds,
  acceptedCount,
  deniedCount,
  totalReferrals,
  respondedCount,
  loading,
}: Props) {
  const { openMetricSlideout } = useReferralsInsightsActions();
  if (loading) {
    return (
      <MetricsContainer>
        <StyledProgress />
      </MetricsContainer>
    );
  }

  const metrics: MetricConfig[] = [
    {
      label: 'Acceptance Rate (%)',
      value: formatPercentage(acceptanceRate),
      color: 'green',
      subtitle: `${acceptedCount} of ${totalReferrals}`,
      onClick: () => openMetricSlideout('acceptance_rate'),
    },
    {
      label: 'Denial Rate (%)',
      value: formatPercentage(denialRate),
      color: 'red',
      subtitle: `${deniedCount} of ${totalReferrals}`,
      onClick: () => openMetricSlideout('denial_rate'),
    },
    {
      label: 'Avg. Response Time',
      value: formatResponseTime(avgResponseTimeSeconds),
      color: 'default',
      subtitle: `Based on ${respondedCount} referrals`,
      onClick: () => openMetricSlideout('response_time'),
    },
  ];

  return (
    <MetricsContainer>
      {metrics.map((metric, i) => (
        <Fragment key={metric.label}>
          {i > 0 && <Divider />}
          <MetricTile $drillable={!!metric.onClick} onClick={metric.onClick}>
            <MetricContent>
              <MetricLabel>{metric.label}</MetricLabel>
              <ValueRow>
                <MetricValue $color={metric.color}>{metric.value}</MetricValue>
                <MetricSubtitle>{metric.subtitle}</MetricSubtitle>
              </ValueRow>
            </MetricContent>
            {metric.onClick && (
              <ChevronWrapper>
                <ChevronRightBold />
              </ChevronWrapper>
            )}
          </MetricTile>
        </Fragment>
      ))}
    </MetricsContainer>
  );
}

const MetricsContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 200px;
  border-left: 1px solid ${({ theme }) => theme.colors.black10};
`;

const MetricTile = styled.div<{ $drillable?: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  padding: 0 24px;
  cursor: ${({ $drillable }) => ($drillable ? 'pointer' : 'default')};

  &:hover {
    background-color: ${({ $drillable, theme }) => ($drillable ? theme.colors.primaryBlue05 : 'transparent')};
  }
`;

const MetricContent = styled.div`
  flex: 1;
`;

const MetricLabel = styled(Label)`
  display: block;
  color: ${({ theme }) => theme.colors.black75};
  margin-bottom: 12px;
`;

const ValueRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const MetricValue = styled.div<{ $color: 'green' | 'red' | 'default' }>`
  font-size: 20px;
  font-weight: var(--font-weight-extra-bold);
  letter-spacing: 0.3px;
  line-height: 24px;
  color: ${({ $color, theme }) => {
    if ($color === 'green') return theme.colors.accentGreen;
    if ($color === 'red') return theme.colors.accentRed;
    return theme.colors.black;
  }};
`;

const MetricSubtitle = styled(Label)`
  color: ${({ theme }) => theme.colors.black50};
`;

const ChevronWrapper = styled.div`
  color: ${({ theme }) => theme.colors.black75};
  display: flex;
  align-items: center;
`;

const Divider = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.colors.black10};
`;

const StyledProgress = styled(CircularProgress)`
  color: ${({ theme }) => theme.colors.primaryBlue};
  margin: auto;
`;
